import { createContext } from 'react';

/**
 * Les ÉTAPES du Planning annuel — lot PA.1 (revue ergonomique, maquette « accueil 2 »
 * du 24/10 et « accueil 3 » du 28/10).
 *
 * La vignette « Planning annuel » de l'accueil s'ouvre sur des étapes numérotées,
 * comme une vignette de suivi du Stage : Préparer (1 · 2 · 3), Construire (4),
 * Diffuser (les deux exports). Les écrans d'avant NE CHANGENT PAS : la barre
 * d'étapes se pose devant eux, et le Paramétrage est seulement coupé en trois.
 *
 * `cle` est la valeur de `?onglet=` dans l'URL. Les anciennes valeurs survivent :
 * `parametrage` (et la route `/setup` qui y renvoie) ouvre l'étape 1, et le
 * Calendrier garde `calendrier`.
 */
export const ETAPES = [
    { cle: 'etape1', n: 1, groupe: 'Préparer', lib: 'Configuration', ic: 'fa-sliders',
      titre: '1 · Configuration — l\'année et les types de journées' },
    { cle: 'etape2', n: 2, lib: 'Types d\'événements', ic: 'fa-calendar-check',
      titre: '2 · Types d\'événements' },
    { cle: 'etape3', n: 3, lib: 'Formations', ic: 'fa-graduation-cap',
      titre: '3 · Formations — niveaux, groupes et heures par semestre' },
    { cle: 'calendrier', n: 4, groupe: 'Construire', lib: 'Calendrier', ic: 'fa-calendar-days', exigeConfig: true },
    { cle: 'export', groupe: 'Diffuser', lib: 'Export PDF Calendrier', ic: 'fa-file-pdf', exigeConfig: true },
    { cle: 'exportevt', lib: 'Export PDF Événements', ic: 'fa-file-lines', exigeConfig: true },
];

/** Les valeurs de `?onglet=` reconnues (l'accueil n'en a pas : c'est l'absence). */
export const ONGLETS = ['accueil', ...ETAPES.map((e) => e.cle)];

/** Ancien nom → nouveau : un signet ou la route `/setup` ne doivent pas tomber dans le vide. */
export const ALIAS_ONGLETS = { parametrage: 'etape1' };

/** Les étapes du Paramétrage (un même écran, coupé en trois). */
export const estEtapeParametrage = (cle) => /^etape[123]$/.test(cle || '');

/**
 * La première étape qui n'est pas faite, sinon le Calendrier.
 * `etat` : la réponse de `GET /api/planning/accueil` (ou null tant qu'elle n'est pas là).
 */
export function premiereAFaire(etat) {
    if (!etat) return 'calendrier';
    const e = ETAPES.find((x) => x.n && !etat.etapes?.[x.n]?.faite);
    return e ? e.cle : 'calendrier';
}

/** Pourquoi une étape n'est pas faite — l'infobulle de son numéro. */
export function motifEtape(n, etat) {
    const e = etat?.etapes?.[n];
    if (!e) return '';
    if (e.faite) return 'Étape faite';
    if (n === 1) return 'À faire : au moins un type de journée';
    if (n === 2) return 'À faire : au moins une catégorie d\'événement';
    if (n === 3) return e.manquants?.length
        ? 'Niveau(x) sans groupe : ' + e.manquants.join(', ')
        : 'À faire : au moins un niveau et un groupe par niveau';
    return e.manquants?.length
        ? 'Groupe(s) sans période cette année : ' + e.manquants.join(', ')
        : 'À faire : au moins un groupe, et une période pour chacun';
}

/**
 * Ce que le module partage avec ses écrans : l'état des étapes, l'année affichée,
 * la configuration prête ou non, et de quoi relire l'état après un enregistrement.
 * Passer par un contexte évite de faire traverser ces données aux quatre écrans
 * existants, qui n'ont pas à les connaître : seule la barre du haut les lit.
 * `bandeau` (lot PA.4) : la barre bleue, repère du volet d'aide qui s'ouvre dessous.
 */
export const PlanningEtapesContext = createContext({
    etat: null,
    anneeLibelle: '',
    configPrete: true,
    rafraichir: () => {},
    bandeau: null,
});
